import { toast } from "sonner"

// Backend error response format
export interface BackendError {
  error: {
    type: string
    message: string
  }
}

// Error types from backend
export const ERROR_TYPES = {
  INVALID_TOKEN: 'InvalidTokenError',
  LLM_PROVIDER: 'LlmProviderError', 
  MISSING_CONFIG: 'MissingConfigError',
  INTERNAL_SERVER: 'InternalServerError'
} as const

// Status code to error action mapping
export interface ErrorHandlerOptions {
  onTokenRefresh?: () => void | Promise<void>
  onRetry?: () => void | Promise<void>
  showToast?: boolean
  customMessage?: string
}

export class ApiError extends Error {
  public status: number
  public errorType: string
  public originalResponse?: Response

  constructor(
    status: number,
    errorType: string,
    message: string,
    originalResponse?: Response
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.errorType = errorType
    this.originalResponse = originalResponse
  }
}

export async function handleApiError(
  response: Response,
  options: ErrorHandlerOptions = {}
): Promise<never> {
  const { onTokenRefresh, onRetry, showToast = true, customMessage } = options

  let errorData: BackendError
  let userMessage: string

  try {
    errorData = await response.json()
    userMessage = errorData.error?.message || 'An unexpected error occurred'
  } catch {
    // If we can't parse the error response, use status-based messages
    userMessage = getDefaultErrorMessage(response.status)
    errorData = {
      error: {
        type: 'UnknownError',
        message: userMessage
      }
    }
  }

  // Use custom message if provided
  if (customMessage) {
    userMessage = customMessage
  }

  // Handle specific status codes
  switch (response.status) {
    case 401:
      if (onTokenRefresh) {
        await onTokenRefresh()
        return Promise.reject(new ApiError(response.status, errorData.error.type, userMessage, response))
      }
      break

    case 429:
      if (onRetry) {
        const rateMessage = "Too many requests. Please wait a few seconds and try again.";
        if (showToast) {
          toast.error(errorData?.error?.message || rateMessage)
        }
        return Promise.reject(new ApiError(response.status, errorData.error.type, userMessage, response))
      }
      break

    case 502:
    case 503:
      // Service unavailable - suggest retry
      if (showToast) {
        toast.error(userMessage, {
          description: "The service is temporarily unavailable. Please try again later.",
        })
      }
      break

    default:
      // Other errors - just show the message
      if (showToast) {
        toast.error(userMessage)
      }
      break
  }

  // Show toast for non-handled cases
  if (showToast && response.status !== 429) {
    toast.error(userMessage)
  }
  throw new ApiError(response.status, errorData.error.type, userMessage, response)
}

function getDefaultErrorMessage(status: number): string {
  switch (status) {
    case 400:
      return 'Invalid request. Please check your input.'
    case 401:
      return 'Your session has expired. Please refresh the page.'
    case 403:
      return 'You do not have permission to perform this action.'
    case 404:
      return 'The requested resource was not found.'
    case 429:
      return 'Too many requests. Please try again later.'
    case 500:
      return 'An internal server error occurred.'
    case 502:
      return 'Service temporarily unavailable.'
    case 503:
      return 'Service is currently under maintenance.'
    default:
      return 'An unexpected error occurred.'
  }
}

// Helper function to show success messages
export function showSuccess(message: string, description?: string) {
  toast.success(message, description ? { description } : undefined)
}

// Helper function to show info messages
export function showInfo(message: string, description?: string) {
  toast.info(message, description ? { description } : undefined)
}

// Helper function to show warning messages
export function showWarning(message: string, description?: string) {
  toast.warning(message, description ? { description } : undefined)
}
